// @flow
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import classNames from 'classnames/bind';
import Modal from '../../../elements/Modal/Modal';
import Loading from '../../../elements/Loading/Loading';
import Select from '../../../elements/Select/Select';
import { FormValidator } from '../../../utils/form';
import UploadBox from '../../order/Sale/components/UploadBox/UploadBox';
import update from 'immutability-helper';

import style from './ReportModal.scss';
import buttonStyle from '../../../assets/styles/button.scss';
import formStyle from '../../../assets/styles/form.scss';
import inputStyle from '../../../assets/styles/input.scss';
import './ReportModal.css';

import type { HTTPError } from '../../../error';
import type { FormValidationResult } from '../../../utils/form';
import type { Option } from '../../../elements/Select/Select';
import type { Report } from '../duck';

type Props = {
  +id?: string;
  +isReporting?: boolean;
  +reportFunc: (report: Report) => void;
  +reportError?: HTTPError;
  +reportSuccess?: boolean;
  +isOpen?: boolean;
  +isDiy?: boolean;
  +handleClose: () => void;
  +resetState: () => void;
  +options: Option[];
  +reportType: 'AVAILABLE_ORDERS' | 'PURCHASER_TRACKING' | 'TRAVELER_TRACKING';
  +color: 'orange' | 'lightBlue';
  +successIntl: string;
  +successPath?: string;
  +goToRedirect: (route: string) => void;
};

type State = {
  reportOptionId: {
    value: string;
    errors?: string[];
  };
  message?: {
    value: string;
    errors?: string[];
  };
  images: {
    valueURI: string;
    name: string;
    error?: boolean;
  }[];
};

const messages = defineMessages({
  selectPlaceholder: {
    id: 'modal.report.select.placeholder',
  },
});

const validFormats = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

export default class ReportModal extends Component<Props, State> {
  formValidator: FormValidator;

  state = {
    reportOptionId: {
      value: '',
    },
    message: {
      value: '',
    },
    images: [{
      valueURI: '',
      name: '',
    }],
  };

  constructor(props: Props) {
    super(props);
    this.formValidator = new FormValidator({
      reportOptionId: { required: 'required' },
    });
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { reportError, isOpen, isDiy } = prevProps;
    const { reportOptionId } = this.state;
    if (!reportError && this.props.reportError) {
      this.props.handleClose();
    }
    if (!isOpen && this.props.isOpen && isDiy) {
      this.setState({ reportOptionId: { value: 'OTHER' } });
    }

    if (prevState.reportOptionId.value !== reportOptionId.value) {
      if (reportOptionId.value === 'OTHER') {
        this.formValidator.replaceFieldValidators({
          message: { required: 'required' },
        });
      } else {
        this.formValidator.replaceFieldValidators({
          message: (null: any),
        });
      }
    }

  }

  handleChangeOption = (option: Option) => {
    this.setState({
      reportOptionId: {
        value: `${option.value}`,
      },
    });
  };

  handleChange = (e: Event) => {
    if (e.target instanceof HTMLInputElement ||
      e.target instanceof HTMLTextAreaElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleFocus = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  };

  handleSubmit = (e: Event) => {
    e.preventDefault();
    const { reportFunc, reportType, id } = this.props;
    const { reportOptionId, images } = this.state;
    const message = this.state.message && this.state.message.value;
    const errors = this.formValidator.validateForm({
      reportOptionId: reportOptionId.value,
      message,
    });

    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors } }));
    } else {
      const report: any = {
        type: reportType,
        orderId: id,
        motive: reportOptionId.value || undefined,
        observation: message || undefined,
        images: images[0].valueURI ? images : undefined,
      };
      reportFunc(report);
    }
  };

  handleCloseModal = () => {
    const { handleClose, isReporting, resetState } = this.props;
    if (!isReporting) {
      this.setState({
        reportOptionId: { value: '' },
        message: { value: '' },
        images: [{ valueURI: '', name: '' }],
      });
      handleClose();
      resetState();
    }
  };

  handleChangeFile = (acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        this.setState((prevState: State) => ({
          images: update(prevState.images, {
            [0]: {
              valueURI: { $set: fileAsDataURL.toString() },
              name: { $set: acceptedFiles[0].name },
            },
          }),
        }));
      };
      reader.readAsDataURL(acceptedFiles[0]);
    }
  };

  handleRejectedFile = (rejectedFiles: File[]) => {
    if (rejectedFiles.length) {
      if (rejectedFiles[0].type === '' || !validFormats.includes(rejectedFiles[0].type) ||
        rejectedFiles[0].size > 3145728) {
        this.setState((prevState: State) => ({
          images: update(prevState.images, {
            [0]: { error: { $set: true } },
          }),
        }));
      }
    }
  };

  handleSuccessButton = () => {
    const { successPath, goToRedirect } = this.props;
    if (successPath) {
      goToRedirect(successPath);
    } else {
      this.handleCloseModal();
    }
  };

  builderScene() {
    const { reportSuccess, color, successIntl, successPath } = this.props;

    if (reportSuccess) {
      return <section className={style.alterContent}>
        <FormattedMessage id="modal.report.success.title">{
          txt => <h2 className={classNames(style[color])}>{txt}</h2>
        }</FormattedMessage>
        <FormattedMessage id={successIntl}>{
          txt => <p>{txt}</p>
        }</FormattedMessage>
        <FormattedMessage id={successPath ? "modal.report.reserve.success.button" :
          "modal.report.close.success.button"}>{
          txt => <button className={classNames(buttonStyle.btn, buttonStyle.greyInverse)}
                         onClick={this.handleSuccessButton}>{txt}</button>
        }</FormattedMessage>
      </section>
    } else {
      const { reportOptionId, message, images } = this.state;
      const { isReporting, options, isDiy, color } = this.props;
      const optionError = reportOptionId.errors
        && FormValidator.buildErrorLabel(reportOptionId.errors, {
          required: 'modal.report.select.error.required',
        });
      const messageError = message && message.errors && FormValidator.buildErrorLabel(message.errors, {
        required: 'modal.report.message.error.required',
      });
      return (
        <form className={classNames(style.reportForm, formStyle.formWrap)}
              onSubmit={this.handleSubmit}>
          <FormattedMessage id="modal.report.title">{
            txt => <h2 className={classNames(style[color])}>{txt}</h2>
          }</FormattedMessage>
          {!isDiy && <section className={style.row}>
            <FormattedMessage id="modal.report.select.label">{
              txt => <p className={style.label}>{txt}:</p>
            }</FormattedMessage>
            <Select value={reportOptionId.value}
                    className={classNames({
                      'light-blue': reportOptionId.value && color === 'lightBlue',
                      'orange': reportOptionId.value && color === 'orange',
                      'light-blue-hover': color === 'lightBlue',
                      'orange-hover': color === 'orange',
                      'red': !!reportOptionId.errors && !!reportOptionId.errors,
                    }, 'report')}
                    handleChange={this.handleChangeOption}
                    searchable={false}
                    placeholder={this.context.intl.formatMessage(messages.selectPlaceholder)}
                    options={options}
                    disabled={isReporting}/>
            {optionError}
          </section>}
          <section className={style.row}>
            <FormattedMessage id="modal.report.textarea.label">{
              txt => <p className={style.label}>{txt}:</p>
            }</FormattedMessage>
            <FormattedMessage id="modal.report.textarea.placeholder">{
              txt => <textarea placeholder={txt}
                               disabled={isReporting}
                               name="message"
                               onFocus={this.handleFocus.bind(this)}
                               onChange={this.handleChange.bind(this)}
                               className={classNames(inputStyle.input,
                                 { [inputStyle.red]: message && message.errors && message.errors.length },
                                 { [inputStyle[color]]: message && !!message.value })}
                               value={message && message.value}/>
            }</FormattedMessage>
            {messageError}
          </section>
          <section className={style.row}>
            <UploadBox error={images[0].error || false}
                       handleChangeFile={(files: File[]) => this.handleChangeFile(files)}
                       handleRejectedFile={(files: File[]) => this.handleRejectedFile(files)}
                       hasInvoice={!!images[0].valueURI}
                       uploadedIntl="modal.report.image.ready"
                       uploadIntl="modal.report.image.upload"/>
          </section>
          <FormattedMessage id="modal.report.button.submit">{
            txt => <button className={classNames(buttonStyle.btn, buttonStyle.red, style.confirm)}
                           disabled={isReporting}
                           onClick={this.handleSubmit}>{txt}</button>
          }</FormattedMessage>
        </form>
      )
    }
  }

  render() {
    const { isReporting, isOpen, color } = this.props;
    return (
      <Modal open={isOpen}
             big
             handleClose={this.handleCloseModal}>
        {isReporting ? <Loading type="sk-three-bounce"
                                color={color}
                                className={style.modalLoading}
                                number={3}/> :
          <section className={style.reportModal}>
            {this.builderScene()}
          </section>
        }
      </Modal>
    );
  }
}

ReportModal.propTypes = {
  id: PropTypes.string,
  isReporting: PropTypes.bool,
  reportFunc: PropTypes.func.isRequired,
  reportError: PropTypes.object,
  reportSuccess: PropTypes.bool,
  isOpen: PropTypes.bool,
  isDiy: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  options: PropTypes.array,
  reportType: PropTypes.string.isRequired,
  color: PropTypes.string,
  successIntl: PropTypes.string,
  successPath: PropTypes.string,
  goToRedirect: PropTypes.func.isRequired,
};

ReportModal.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/report/components/ReportModal.jsx